/**
 * 시도(광역자치단체) 정식 명칭·축약형 매핑.
 *
 * 지역 랜딩 URL(/facilities/:sido/...)의 시도 세그먼트 정규화에 사용한다.
 * - 정식 17개 목록: RegionGrid 의 REGION_ORDER 와 동일한 정식 명칭
 * - 축약형 매핑: 백엔드 KakaoAddressClient.SIDO_ABBREV 의 역방향 + 구명칭(강원도·전라북도)
 *
 * 배경: HIRA 임포트 요양병원 데이터가 축약형 시도(경기·전남 등)로 중복 지역 URL 을
 * 양산했다. 축약형 시도 URL 은 정식형으로 301 정규화하고, 매핑 불가 시도는 404 처리한다.
 */

/** 정식 시도명 17개 */
export const OFFICIAL_SIDOS = [
  '서울특별시', '부산광역시', '대구광역시', '인천광역시', '광주광역시',
  '대전광역시', '울산광역시', '세종특별자치시', '경기도', '강원특별자치도',
  '충청북도', '충청남도', '전북특별자치도', '전라남도', '경상북도',
  '경상남도', '제주특별자치도',
] as const

const OFFICIAL_SIDO_SET = new Set<string>(OFFICIAL_SIDOS)

/** 축약형/구명칭 → 정식 시도명 */
export const SIDO_ALIAS_TO_OFFICIAL: Record<string, string> = {
  서울: '서울특별시',
  부산: '부산광역시',
  대구: '대구광역시',
  인천: '인천광역시',
  광주: '광주광역시',
  대전: '대전광역시',
  울산: '울산광역시',
  세종: '세종특별자치시',
  경기: '경기도',
  강원: '강원특별자치도',
  강원도: '강원특별자치도',
  충북: '충청북도',
  충남: '충청남도',
  전북: '전북특별자치도',
  전라북도: '전북특별자치도',
  전남: '전라남도',
  경북: '경상북도',
  경남: '경상남도',
  제주: '제주특별자치도',
}

/**
 * 전라남도 22개 시·군 — 광주광역시로 잘못 귀속된 지역 URL 정규화용.
 * 광주 5개 구(동구·서구·남구·북구·광산구)와 이름이 겹치지 않는다.
 * 영구지침: docs/develop/seo/regional-page-spec.md §2.3
 */
const JEONNAM_SIGUNGUS = new Set<string>([
  '목포시', '여수시', '순천시', '나주시', '광양시', '담양군', '곡성군', '구례군', '고흥군', '보성군', '화순군',
  '장흥군', '강진군', '해남군', '영암군', '무안군', '함평군', '영광군', '장성군', '완도군', '진도군', '신안군',
])

/**
 * 잘못 귀속된 시·도면 올바른 시·도명을, 아니면 undefined 를 돌려준다.
 *
 * 배경: 잡 A 가 검색 API 코드 12(광주+전남 혼합)를 광주로만 해석해 전남 시설을 광주광역시에 저장했고,
 * `광주광역시/순천시/요양원` 같은 URL 262개가 사이트맵에 올랐다(2026-09-15 데이터 보정).
 * 숫자 코드 시군구(`155` 등)는 목록에 없어 대상이 아니다.
 */
export function correctMisattributedSido(sido?: string, sigungu?: string): string | undefined {
  if (sido === '광주광역시' && sigungu && JEONNAM_SIGUNGUS.has(sigungu)) return '전라남도'
  return undefined
}

/** 정식 시도명 여부 */
export function isOfficialSido(sido: string): boolean {
  return OFFICIAL_SIDO_SET.has(sido)
}

/** 축약형/구명칭이면 정식 시도명 반환, 매핑 불가면 undefined */
export function toOfficialSido(sido: string): string | undefined {
  return SIDO_ALIAS_TO_OFFICIAL[sido]
}

/**
 * 시도·시군구·동을 공백으로 잇되, 동이 시군구 끝에 이미 포함된 경우 중복을 제거한다.
 * 구(區)가 있는 시(용인·성남·고양 등)의 동 필드에 구 이름이 잘못 유입된 데이터
 * (예: sigungu="용인시 수지구", dong="수지구" → "용인시 수지구 수지구") 방어.
 */
export function formatRegionPath(
  sido?: string | null,
  sigungu?: string | null,
  dong?: string | null,
): string {
  const sgg = sigungu?.trim() || ''
  const dg = dong?.trim() || ''
  const parts = [sido?.trim() || '', sgg].filter(Boolean)
  if (dg && !(sgg && sgg.endsWith(dg))) parts.push(dg)
  return parts.join(' ')
}

/** 정식 시도명 → 약칭 (제목 라벨용). */
const SIDO_SHORT: Record<string, string> = {
  서울특별시: '서울', 부산광역시: '부산', 대구광역시: '대구', 인천광역시: '인천', 광주광역시: '광주',
  대전광역시: '대전', 울산광역시: '울산', 세종특별자치시: '세종', 경기도: '경기', 강원특별자치도: '강원',
  충청북도: '충북', 충청남도: '충남', 전북특별자치도: '전북', 전라남도: '전남', 경상북도: '경북',
  경상남도: '경남', 제주특별자치도: '제주',
}

/**
 * 여러 시도에 같은 이름이 있는 시군구 — 제목에 시도 약칭을 붙여 구분한다.
 * 행정구역 이름이라 사실상 고정이다. 지역 목록 API 로 매번 계산하면 SSR 에 데이터가 없을 때 제목이 흔들린다.
 * (데이터 오귀속으로 서울 아래에 보이는 `파주시` 같은 이름은 넣지 않는다 — 데이터로 고친다.)
 * 영구지침: docs/develop/seo/regional-page-spec.md §4.4.1
 */
const DUPLICATE_SIGUNGU_NAMES = new Set<string>(['동구', '중구', '서구', '남구', '북구', '강서구', '고성군'])

/**
 * 지역 페이지 제목·H1 의 지역 라벨.
 *
 * - 시도: 정식명 / 시군구: 이름 / 동: `{시군구} {동}`
 * - 시군구가 여러 시도에 있는 이름이면 앞에 시도 약칭 (`대구 중구`, `서울 중구 신당동`)
 *
 * 배경: 동 이름만 쓰면 `중동`(8곳)·`교동`(6곳)이, 시군구 이름만 쓰면 `중구`·`동구`(각 6곳)가
 * 서로 같은 제목이 되어 지역 페이지 694개가 중복 제목이었다(2026-09-15 실측).
 * 영구지침: docs/develop/seo/regional-page-spec.md §4.4.1
 */
export function regionHeadingLabel({
  sido,
  sigungu,
  dong,
}: {
  sido?: string | null
  sigungu?: string | null
  dong?: string | null
}): string {
  const sd = sido?.trim() || ''
  const sgg = sigungu?.trim() || ''
  if (!sgg) return sd

  const short = SIDO_SHORT[sd]
  const sggLabel = DUPLICATE_SIGUNGU_NAMES.has(sgg) && short ? `${short} ${sgg}` : sgg
  // 동이 시군구 끝에 이미 있으면 붙이지 않는다 (formatRegionPath 와 같은 규칙)
  return formatRegionPath(undefined, sggLabel, dong)
}
